/**
 * /f/:slug — pagina PUBLICA de resposta a um formulario.
 *
 * Fora do AuthenticatedShell (padrao das paginas /p/*): faz `fetch` direto
 * via services/formularios (sem AuthContext). Renderiza pelo PublicFormView
 * (o mesmo componente que a aba Design pre-visualiza), com a cor do tema e
 * o design do config. No submit faz POST /responder e mostra a mensagem de
 * sucesso (ou redireciona para redirect_url). Trata 404 (form inativo/
 * inexistente) com tela amigavel.
 *
 * Query string suportada: `?convite=<token>` (link pessoal do formulario
 * ENRIQUECER_LEAD, gerado no cockpit do lead — FORM-12/FORM-16; o antigo
 * `?lead=` deixou de existir) e `?utm_source=...&utm_medium=...&utm_campaign=...`
 * (repassados no POST como `utm`). Sem convite valido, a pagina avisa antes
 * de mostrar os campos.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";

import { buscarCepPublico } from "../../services/enderecos";
import {
  enviarArquivoFormularioPublico,
  responderFormularioPublico,
  visualizarFormularioPublico,
} from "../../services/formularios";
import PublicFormView from "../Formularios/PublicFormView";
import { varsDoDesign } from "../Formularios/designConfig";
import { urlHttpSegura } from "../Formularios/urlRedirect";
import {
  PublicCard,
  PublicPage,
  PublicState,
  PublicTopbar,
} from "../Formularios/styles";

// Extrai os parametros utm_* da query string num objeto {source, medium, ...}.
function extrairUtm(searchParams) {
  const utm = {};
  searchParams.forEach((valor, chave) => {
    if (chave.startsWith("utm_") && valor) {
      utm[chave.replace(/^utm_/, "")] = valor;
    }
  });
  return utm;
}

export default function FormularioPublico() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();

  const [estado, setEstado] = useState({ loading: true, erro: null, dados: null });
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(null);

  const utm = useMemo(() => extrairUtm(searchParams), [searchParams]);
  const convite = searchParams.get("convite") || undefined;

  useEffect(() => {
    if (!slug) {
      setEstado({ loading: false, erro: { status: 404 }, dados: null });
      return;
    }
    let vivo = true;
    visualizarFormularioPublico(slug, { convite })
      .then((dados) => {
        if (vivo) setEstado({ loading: false, erro: null, dados });
      })
      .catch((err) => {
        if (vivo) {
          setEstado({
            loading: false,
            erro: { status: err?.status, mensagem: err?.message },
            dados: null,
          });
        }
      });
    return () => {
      vivo = false;
    };
  }, [slug, convite]);

  const cor = estado.dados?.config?.tema_cor;
  // A tela de sucesso veste o mesmo design da pagina do formulario.
  const vars = useMemo(() => varsDoDesign(estado.dados?.config?.design), [estado.dados]);
  // Busca de CEP presa a este formulario: sem login, com limite por IP e so
  // enquanto ele estiver ativo (ADR-0071).
  const buscarCepDoFormulario = useCallback((cep) => buscarCepPublico(slug, cep), [slug]);
  // Upload do campo ARQUIVO (FORM-05): cada arquivo sobe na hora e o envio
  // cita o token; no ENRIQUECER o convite acompanha o upload.
  const uploadArquivo = useCallback(
    (arquivo) => enviarArquivoFormularioPublico(slug, arquivo, { convite }),
    [slug, convite]
  );

  const handleSubmit = async (dados, antispam) => {
    setEnviando(true);
    try {
      const res = await responderFormularioPublico(slug, {
        dados,
        utm,
        convite,
        // Honeypot e tempo de preenchimento medidos pelo FormRenderer (FORM-49).
        antispam,
      });
      // Redirect tem precedencia sobre a tela de sucesso, se configurado.
      // Esta pagina roda na origem do ERP, com a sessao de quem esta logado:
      // so navega para http(s) absoluta, mesmo que o servidor ja filtre
      // (FORM-02). Fora disso, fica a mensagem de sucesso.
      const destino = urlHttpSegura(res?.redirect_url);
      if (destino) {
        window.location.assign(destino);
        return;
      }
      setSucesso(
        res?.mensagem_sucesso ||
          estado.dados?.config?.mensagem_sucesso ||
          "Recebemos sua resposta. Obrigado!"
      );
    } catch (err) {
      // Propaga para o FormRenderer exibir o erro inline (banner).
      throw err;
    } finally {
      setEnviando(false);
    }
  };

  // ---- Loading ----
  if (estado.loading) {
    return (
      <PublicPage>
        <PublicTopbar />
        <PublicCard>
          <PublicState>
            <p>Carregando...</p>
          </PublicState>
        </PublicCard>
      </PublicPage>
    );
  }

  // ---- Erro / inativo / inexistente ----
  if (estado.erro) {
    const status = estado.erro.status;
    let titulo = "Formulário indisponível";
    let msg =
      "Este formulário não está disponível no momento. Verifique o link ou entre em contato com quem o enviou.";
    if (status === 404) {
      titulo = "Formulário não encontrado";
      msg =
        "O link pode estar incorreto ou o formulário foi removido. Confira o endereço e tente novamente.";
    } else if (status === 410) {
      titulo = "Formulário encerrado";
      msg = "Este formulário não está mais aceitando respostas.";
    } else if (status === 429) {
      titulo = "Muitas tentativas";
      msg = "Por segurança, aguarde alguns instantes e tente novamente.";
    }
    return (
      <PublicPage>
        <PublicTopbar />
        <PublicCard>
          <PublicState>
            <FiAlertCircle size={40} aria-hidden="true" />
            <h2>{titulo}</h2>
            <p>{msg}</p>
          </PublicState>
        </PublicCard>
      </PublicPage>
    );
  }

  // ---- Link pessoal sem convite valido (FORM-12) ----
  // O aviso vem ANTES dos campos: o cliente nao preenche tudo para descobrir
  // no envio que o link nao vale.
  if (estado.dados?.exige_convite && !estado.dados?.convite?.valido) {
    return (
      <PublicPage $accent={cor} style={vars}>
        <PublicTopbar />
        <PublicCard>
          <PublicState>
            <FiAlertCircle size={40} aria-hidden="true" />
            <h2>Link pessoal</h2>
            <p>
              {estado.dados?.convite?.mensagem ||
                "Este link é pessoal. Peça um novo link à loja para completar seu cadastro."}
            </p>
          </PublicState>
        </PublicCard>
      </PublicPage>
    );
  }

  // ---- Sucesso ----
  if (sucesso) {
    return (
      <PublicPage $accent={cor} style={vars}>
        <PublicTopbar />
        <PublicCard>
          <PublicState>
            <FiCheckCircle size={44} aria-hidden="true" />
            <h2>Tudo certo!</h2>
            <p>{sucesso}</p>
          </PublicState>
        </PublicCard>
      </PublicPage>
    );
  }

  // ---- Formulario ----
  return (
    <PublicFormView
      dados={estado.dados}
      submitting={enviando}
      onSubmit={handleSubmit}
      buscarCep={buscarCepDoFormulario}
      uploadArquivo={uploadArquivo}
    />
  );
}
